import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Eye, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useStudioStore } from '~entities/studios/model/studio-store';

import { TextEditor } from '~pages/settings/email-form-config/text-editor';

import { useUpdateEmailTemplateMutation } from '~shared/api/email-template';
import { KEYS } from '~shared/api/query-keys';
import { useGetGHLLocationDetails } from '~shared/api/get-ghl-location-details';
import { Button } from '~shared/shadcn/ui/button';
import { Toaster } from '~shared/shadcn/ui/sonner';

import { BrandBar } from './components/BrandBar';
import { EmailPreviewDrawer } from './components/EmailPreviewDrawer';
import { SubjectField } from './components/SubjectField';
import { SystemFooterPreview } from './components/SystemFooterPreview';
import { extractBothImagesFromContent } from './extract-both-images-from-content';
import { sanitizeHtml } from './utils/sanitizeHtml';

interface EmailTemplateEditorPageProps {
  returnTo?: string;
  returnStep?: number;
  initialSubject: string;
  initialStudioName: string;
  studioLogoUrl?: string;
  initialHtmlContent: string;
  initialParagraph1Image: string;
  initialParagraph2Image: string;
  isLoadingTemplate?: boolean;
}

export const EmailTemplateEditorPage = ({
  returnTo,
  returnStep,
  initialSubject,
  initialStudioName,
  studioLogoUrl,
  initialHtmlContent,
  initialParagraph1Image,
  initialParagraph2Image,
  isLoadingTemplate = false,
}: EmailTemplateEditorPageProps) => {
  const navigate = useNavigate();
  const studioId = useStudioStore((state) => state.currentStudioId);
  const { data: ghlLocation } = useGetGHLLocationDetails();
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState(initialSubject);
  const [htmlContent, setHtmlContent] = useState(initialHtmlContent);
  const [subjectError, setSubjectError] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    setSubject(initialSubject);
    setHtmlContent(initialHtmlContent);
    setSubjectError('');
  }, [initialSubject, initialHtmlContent]);

  const {
    mutateAsync: updateEmailTemplate,
    isPending: isUpdatingEmailTemplate,
  } = useUpdateEmailTemplateMutation();

  const handleContentChange = (html: string) => {
    setHtmlContent(html);
  };

  const handleSubjectChange = (value: string) => {
    setSubject(value);
    setSubjectError('');
  };

  const validate = (): boolean => {
    let isValid = true;

    if (!subject.trim()) {
      setSubjectError('Subject is required');
      isValid = false;
    }

    if (htmlContent.length === 0 || htmlContent === '<p></p>') {
      toast.error('Email content cannot be empty');
      isValid = false;
    }

    return isValid;
  };

  const base64ToFile = (base64String: string, filename: string): File => {
    // Remove the data:image/xxx;base64, prefix
    const base64Content = base64String.split(',')[1];
    // Get the mime type from the base64 string
    const mimeType = base64String.split(';')[0]?.split(':')[1];

    // Convert base64 to binary
    const binaryStr = atob(base64Content ?? '');
    const len = binaryStr.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    // Create Blob and then File
    const blob = new Blob([bytes], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  };

  const blobUrlToBase64 = async (blobUrl: string): Promise<string> => {
    try {
      // Fetch the blob from the URL
      const response = await fetch(blobUrl);
      const blob = await response.blob();

      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          resolve(base64String);
        };
        reader.onerror = () => {
          reject(new Error('Failed to read blob as data URL'));
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Failed to convert blob URL to base64:', error);
      throw new Error(
        `Failed to convert blob URL: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      // Extract both images at once from the content
      const result = extractBothImagesFromContent(
        htmlContent,
        initialParagraph1Image,
        initialParagraph2Image,
      );
      const sanitized = sanitizeHtml(result.text);

      // Create FormData for the API
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('paragraph1', sanitized); // Send all HTML content in paragraph1
      formData.append('paragraph2', ''); // Send empty paragraph2 as requested

      const appendExtractedImage = async (
        raw: string | undefined,
        field: 'paragraph1Image' | 'paragraph2Image',
      ) => {
        if (!raw) return;
        if (raw.startsWith('data:image/')) {
          formData.append(field, base64ToFile(raw, `${field}.png`));
          return;
        }
        if (raw.startsWith('blob:')) {
          const base64Data = await blobUrlToBase64(raw);
          formData.append(field, base64ToFile(base64Data, `${field}.png`));
          return;
        }
        throw new Error(`Unsupported ${field} format`);
      };

      if (sanitized.includes('blob:')) {
        toast.error('Could not save email template', {
          description: 'An image is still loading. Wait a moment and save again.',
        });
        return;
      }

      await appendExtractedImage(result.para1Image, 'paragraph1Image');
      await appendExtractedImage(result.para2Image, 'paragraph2Image');

      // Call the API
      await updateEmailTemplate({ template: formData });

      await queryClient.invalidateQueries({
        queryKey: [KEYS.EMAIL_TEMPLATE, studioId],
      });
      toast.success('Email template saved');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Could not save email template');
    }
  };

  const handlePreview = () => {
    if (!validate()) return;
    setIsPreviewOpen(true);
  };

  const handleBack = () => {
    if (returnTo === '/import-data' && returnStep != null) {
      void navigate({
        to: '/import-data',
        search: { step: returnStep > 0 ? returnStep : undefined },
      });
      return;
    }

    if (returnTo) {
      void navigate({ to: returnTo });
      return;
    }

    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
      return;
    }

    void navigate({ to: '/dashboard' });
  };

  return (
    <div className='min-h-screen bg-[#F9FAFB] pb-24 sm:pb-8'>
      <div className='mx-auto max-w-3xl px-4 py-8'>
        {/* Header */}
        <div className='mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <button
              type='button'
              onClick={handleBack}
              className='mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground'
            >
              <ArrowLeft className='size-4' />
              Back
            </button>
            <h1 className='text-2xl font-semibold'>Email Template Editor</h1>
            <p className='mt-1 text-sm text-muted-foreground'>
              Customize the onboarding email sent to new parents
            </p>
          </div>

          {/* {hasUnsavedChanges && (
            <span className='text-xs text-amber-600'>Unsaved changes</span>
          )} */}
        </div>

        {/* Editor sections */}
        <div className='space-y-6'>
          {/* Brand & Logo */}
          <BrandBar logoUrl={studioLogoUrl} studioName={initialStudioName} />

          {/* Subject */}
          <SubjectField
            value={subject}
            onChange={handleSubjectChange}
            error={subjectError}
          />

          {/* Content Editor */}
          {isLoadingTemplate ? (
            <div className='flex min-h-[200px] items-center justify-center rounded-lg border'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          ) : (
            <TextEditor
              content={htmlContent}
              onChangeContent={handleContentChange}
            />
          )}

          {/* System Footer Preview */}
          <SystemFooterPreview />

          {/* Actions */}
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:space-x-2'>
            <Button
              type='button'
              variant='outline'
              onClick={handlePreview}
              className='w-full sm:w-auto'
            >
              <Eye />
              Preview
            </Button>

            <Button
              type='button'
              onClick={() => handleSave()}
              disabled={isUpdatingEmailTemplate}
              className='w-full sm:w-auto'
              variant='outline'
            >
              {isUpdatingEmailTemplate ? (
                <Loader2 className='animate-spin' />
              ) : (
                <Save />
              )}
              Save
            </Button>
          </div>

          {/* Mobile sticky bottom bar */}
          <div className='fixed inset-x-0 bottom-0 border-t bg-white p-4 shadow-lg sm:hidden'>
            <div className='flex gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={handlePreview}
                className='flex-1'
                size='lg'
              >
                <Eye className='mr-2 size-4' />
                Preview
              </Button>

              <Button
                type='button'
                onClick={async () => {
                  await handleSave();
                  toast.success('Saved! You can now navigate away.');
                }}
                disabled={isUpdatingEmailTemplate}
                className='flex-1 bg-[#FFC300] text-[#000814] hover:bg-[#FFD60A]'
                size='lg'
              >
                Save & Exit
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Drawer */}
      <EmailPreviewDrawer
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        subject={subject}
        htmlContent={htmlContent}
        logoUrl={studioLogoUrl}
        studioName={initialStudioName}
        supportEmail={ghlLocation?.email}
      />

      <Toaster />
    </div>
  );
};
